import axios from "axios";
import React, { Suspense, useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import Sectiontitle from "../components/Sectiontitle";
import Socialicons from "../components/Socialicons";
import Spinner from "../components/Spinner";

function Contact() {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [emailAddress, setEmailAddress] = useState([]);
  const [address, setAddress] = useState([]);
  const [socialProfiles, setSocialProfiles] = useState([]);
  const [formdata, setFormdata] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [error, setError] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false); // <-- Spinner state

  const submitHandler = async (event) => {
    event.preventDefault();

    // Simple validation
    if (!formdata.name || !formdata.email || !formdata.subject || !formdata.message) {
      setError(true);
      setMessage("Please fill out all fields.");
      return;
    }

    // Prepare form data
    const data = new FormData();
    Object.keys(formdata).forEach((key) => data.append(key, formdata[key]));

    try {
      setLoading(true); // Show spinner
      const response = await axios.post("/mail.php", data); // Ensure mail.php path is correct
      setLoading(false);

      if (response.data.success) {
        setError(false);
        setMessage("Your message has been sent!");
        setFormdata({ name: "", email: "", subject: "", message: "" }); // Reset form
      } else {
        setError(true);
        setMessage(response.data.error || "Failed to send message.");
      }
    } catch (err) {
      setLoading(false);
      setError(true);
      setMessage("Server error: unable to send message.");
    }
  };

  const handleChange = (event) => {
    setFormdata({
      ...formdata,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const numberFormatter = (number) => number;

  const handleAlerts = () => {
    if (message) {
      return (
        <div className={`alert mt-4 ${error ? "alert-danger" : "alert-success"}`}>
          {message}
        </div>
      );
    }
    return null;
  };

  useEffect(() => {
    axios.get("/api/contactinfo").then((response) => {
      setPhoneNumbers(response.data.phoneNumbers);
      setEmailAddress(response.data.emailAddress);
      setAddress(response.data.address);
      setSocialProfiles(response.data.socialProfiles);
    });
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Paul Ritter | Contact | Web Developer &amp; Photographer</title>
        <meta
          name="description"
          content="Contact me with any photography or development project ideas!"
        />
      </Helmet>
      <Suspense fallback={<Spinner />}>
        <div className="mi-contact-area mi-section mi-padding-top mi-padding-bottom">
          <div className="container">
            <Sectiontitle title="Contact Me" />
            <div className="row">
              <div className="col-lg-6">
                <div className="mi-contact-formwrapper">
                  <h4>Let's connect <span className="color-theme">&#58;&#41;</span></h4>
                  <p>Do you have an idea? Take a moment to share it with me!</p>
                  <form
                    action="#"
                    className="mi-form mi-contact-form"
                    onSubmit={submitHandler}
                  >
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-name">Enter your name *</label>
                      <input
                        onChange={handleChange}
                        type="text"
                        name="name"
                        id="contact-form-name"
                        value={formdata.name}
                        disabled={loading} // Disable while sending
                      />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-email">Enter your email *</label>
                      <input
                        onChange={handleChange}
                        type="text"
                        name="email"
                        id="contact-form-email"
                        value={formdata.email}
                        disabled={loading}
                      />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-subject">Enter your subject *</label>
                      <input
                        onChange={handleChange}
                        type="text"
                        name="subject"
                        id="contact-form-subject"
                        value={formdata.subject}
                        disabled={loading}
                      />
                    </div>
                    <div className="mi-form-field">
                      <label htmlFor="contact-form-message">Enter your Message *</label>
                      <textarea
                        onChange={handleChange}
                        name="message"
                        id="contact-form-message"
                        cols="30"
                        rows="6"
                        value={formdata.message}
                        disabled={loading}
                      ></textarea>
                    </div>
                    <div className="mi-form-field">
                      <button className="mi-button" type="submit" disabled={loading}>
                        {loading ? "Sending..." : "Send Mail"}
                      </button>
                    </div>
                  </form>
                  {handleAlerts()}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mi-contact-info">
                  {address && (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon"><Icon.MapPin /></span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Based in</h6>
                        <p>{address}</p>
                      </div>
                    </div>
                  )}
                  {emailAddress.length > 0 && (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon"><Icon.Mail /></span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Email</h6>
                        {emailAddress.map((email) => (
                          <p key={email}><a href={`mailto:${email}`}>{email}</a></p>
                        ))}
                      </div>
                    </div>
                  )}
                  {phoneNumbers.length > 0 && (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon"><Icon.Phone /></span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Phone</h6>
                        {phoneNumbers.map((phoneNumber) => (
                          <p key={phoneNumber}><a href={numberFormatter(phoneNumber)}>{phoneNumber}</a></p>
                        ))}
                      </div>
                    </div>
                  )}
                  {socialProfiles.length > 0 && (
                    <div className="mi-contact-infoblock">
                      <span className="mi-contact-infoblock-icon"><Icon.UserPlus /></span>
                      <div className="mi-contact-infoblock-content">
                        <h6>Connect with me</h6>
                        <Socialicons bordered />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Suspense>
    </Layout>
  );
}

export default Contact;
